import { useEffect, useRef } from 'react';
import { useSubroute } from '../../lib/subroute.js';
import { useSession } from '../../lib/auth/session.js';
import { useTenantInvoices, useTenantInvoice, usePayInvoice } from '../../lib/api/hooks-invoices.js';
import { usePayments, isAwaiting } from '../../lib/api/hooks-payments.js';
import { downloadFile } from '../../lib/api/download.js';
import { TableSkeleton, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { toastError } from '../../components/ui/Toast.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatMoney } from '../../lib/format.js';
import { InvoiceBadge, InvoiceDocument, docDate, printDocument } from './InvoiceDocument.js';
import { toPayfast } from './Payments.js';

/** An account's invoices, or one of them below the list: /my/billing/invoices/INV00012. */
export function InvoicesTab({ tenantId }: { tenantId: string | null }) {
  const route = useSubroute();
  const open = route.rest[0] ?? null;
  return open
    ? <TenantInvoiceView tenantId={tenantId} number={open} onBack={() => route.go([])} />
    : <TenantInvoiceList tenantId={tenantId} onOpen={(number) => route.go([number])} />;
}

function TenantInvoiceList({ tenantId, onOpen }: {
  tenantId: string | null;
  onOpen: (number: string) => void;
}) {
  const invoices = useTenantInvoices(tenantId);
  if (invoices.error) return <ErrorBanner error={invoices.error} />;

  return (
    <div className="arc-card">
      <div className="arc-card-head">
        <h2 className="arc-h2">Invoices</h2>
      </div>
      <div className="arc-table-wrap">
        <table className="arc-table">
          <thead>
            <tr>
              <th>Invoice</th><th>Date</th><th>Due</th><th>Status</th>
              <th className="arc-td-right">Total</th>
              <th className="arc-td-right">Due now</th>
            </tr>
          </thead>
          {invoices.data === undefined ? <TableSkeleton cols={6} /> : (
            <tbody>
              {invoices.data?.data.map((i) => (
                <tr key={i.id} className="arc-clickable" onClick={() => i.number && onOpen(i.number)}>
                  <td className="arc-mono" style={{ fontWeight: 600 }}>{i.number}</td>
                  <td className="arc-num arc-subtle">{docDate(i.issue_date)}</td>
                  <td className="arc-num arc-subtle">{docDate(i.due_date)}</td>
                  <td><InvoiceBadge invoice={i} /></td>
                  <td className="arc-td-right arc-num">{formatMoney(i.total, i.currency)}</td>
                  <td className="arc-td-right arc-num" style={{ fontWeight: 550 }}>
                    {formatMoney(i.amount_due, i.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      {invoices.data !== undefined && invoices.data?.data.length === 0 && (
        <Empty icon="invoice" title="No invoices yet" hint="Invoices appear here once they are issued." />
      )}
    </div>
  );
}

function TenantInvoiceView({ tenantId, number, onBack }: {
  tenantId: string | null;
  number: string;
  onBack: () => void;
}) {
  const can = useSession((s) => s.can);
  const invoice = useTenantInvoice(tenantId, number);
  const payments = usePayments(tenantId);
  const pay = usePayInvoice(tenantId ?? '');
  const i = invoice.data;

  // A card payment for this invoice on its way from PayFast. The payments list
  // polls until it lands (usePayments), and the invoice is read again then.
  const awaiting = !!i && !!payments.data?.data.some((p) => p.invoice_id === i.id && isAwaiting(p));
  const wasAwaiting = useRef(awaiting);
  const refetch = invoice.refetch;
  useEffect(() => {
    if (wasAwaiting.current && !awaiting) void refetch();
    wasAwaiting.current = awaiting;
  }, [awaiting, refetch]);

  const download = (path: string, filename: string) => {
    void downloadFile(path, filename).catch(toastError);
  };

  const payable =!!i && (i.status === 'issued' || i.status === 'part_paid') && Number(i.amount_due) > 0;
  const leaving = pay.isPending || pay.isSuccess;
  const base = `/v1/tenants/${tenantId}/billing`;

  return (
    <>
      <div className="arc-row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onBack}>
          <Icon name="arrowLeft" size={13} /> Invoices
        </button>
        {awaiting && <span className="arc-muted">Waiting for PayFast to confirm a payment…</span>}
        <div className="arc-spacer" />
        {i && payable && tenantId && can('payment:make') && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={leaving}
            onClick={() => pay.mutate(number, { onSuccess: toPayfast })}>
            {leaving ? 'Opening PayFast…' : `Pay ${formatMoney(i.amount_due, i.currency)} by card`}
          </button>
        )}
        {i && (
          <button type="button" className="arc-btn arc-btn-sm"
            onClick={() => download(`${base}/invoices/${encodeURIComponent(number)}/pdf`, `${number}.pdf`)}>
            Download PDF
          </button>
        )}
        {i && <button type="button" className="arc-btn arc-btn-sm" onClick={printDocument}>Print</button>}
      </div>

      {invoice.isLoading
        ? <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 320 }} /></div>
        : invoice.error
          ? <ErrorBanner error={invoice.error} />
          : i ? (
            <InvoiceDocument invoice={i} creditNoteAction={(note) => (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => download(`${base}/credit-notes/${encodeURIComponent(note.number)}/pdf`, `${note.number}.pdf`)}>
                PDF
              </button>
            )} />
          ) : null}
    </>
  );
}
